import { useState } from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";

interface SamplePromptRowProps {
  index: number;
  value: string;
  editing: boolean;
  saving: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onSave: (value: string) => void;
  onCancel: () => void;
  onDelete: () => void;
}

const SamplePromptRow = ({
  index,
  value,
  editing,
  saving,
  disabled,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}: SamplePromptRowProps) => {
  // Draft state is initialized from `value` on mount.
  // Parent re-keys this component on edit toggle to reset draft state.
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <div className="py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Prompt {index + 1}</span>
          <div className="flex items-center gap-1">
            <div className="size-8 flex items-center justify-center opacity-30" aria-hidden="true">
              <Pencil className="size-4" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={onDelete}
              disabled={disabled || saving}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={300}
          disabled={saving}
          className="mt-2 min-h-[4.5rem]"
          autoFocus
        />
        <div className="flex gap-2 mt-3">
          <Button
            size="sm"
            onClick={() => onSave(draft)}
            disabled={saving || draft.trim() === "" || draft === value}
          >
            {saving && <Loader2 className="size-4 mr-1 animate-spin" />}
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between py-3">
      <div className="min-w-0 flex-1 mr-2">
        <p className="text-sm font-medium">Prompt {index + 1}</p>
        <p className="text-sm text-muted-foreground line-clamp-1">{value}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="icon" className="size-8" onClick={onEdit} disabled={disabled}>
          <Pencil className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onDelete}
          disabled={disabled}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default SamplePromptRow;
